import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActionsVisibilityContext } from '../ActionsVisibilityContext';
import { GridBorderState, ICanDropOutcome, IGridDropPosition } from './types';
import { useDndGridInternalContext } from './context';
import { areDropPositionsEqual } from './utils';
import { IBorderActionMenuProps } from './BorderActionsMenu/BorderActionsMenu';

const getBorderState = (
  disabled: boolean,
  isThisDropZoneActive: boolean,
  currentDragHandle: ICanDropOutcome,
  canResize: boolean,
  actionsMenuShown: boolean,
) => {
  if (disabled) {
    return GridBorderState.Inactive;
  } else if (isThisDropZoneActive) {
    return currentDragHandle.canDrop
      ? GridBorderState.EnabledDroppable
      : GridBorderState.DisabledDroppable;
  } else if (actionsMenuShown) {
    return canResize ? GridBorderState.Draggable : GridBorderState.Muted;
  } else {
    return GridBorderState.Inactive;
  }
};

type IHookProps = {
  canResize: boolean;
  position: IGridDropPosition;
};

export const useBorderActions = ({ canResize, position }: IHookProps) => {
  const {
    currentDropPosition,
    currentCanDrop,
    disabled,
    borderMenuItemsFactory,
  } = useDndGridInternalContext();

  const isThisDropZoneActive = areDropPositionsEqual(
    currentDropPosition,
    position,
  );

  const actionMenuItems = useMemo(
    () => borderMenuItemsFactory?.(position),
    [borderMenuItemsFactory, position],
  );

  const { showActions, hideActions, actionsVisible } =
    useActionsVisibilityContext();
  const [isActionMenuPinned, setIsActionMenuPinned] = useState(false);
  const isActionsMenuShown =
    (actionsVisible || isActionMenuPinned) && !disabled;

  const borderState = getBorderState(
    disabled,
    isThisDropZoneActive,
    currentCanDrop,
    canResize,
    isActionsMenuShown,
  );

  const pinActionsMenu = useCallback(() => setIsActionMenuPinned(true), []);
  const closeActionsMenu = useCallback(() => {
    setIsActionMenuPinned(false);
    hideActions();
  }, [hideActions]);

  // If the actions menu was pinned and the user starts dragging over this drop zone, we switch the border to
  // drag mode, and we must close the menu - otherwise once the user exits the drop zone, the border will remain pinned
  useEffect(() => {
    if (isThisDropZoneActive) {
      closeActionsMenu();
    }
  }, [isThisDropZoneActive, closeActionsMenu]);

  return {
    borderState,
    showActions,
    hideActions,
    actionsMenuProps: {
      isActionsMenuShown,
      pinActionsMenu,
      closeActionsMenu,
      items: actionMenuItems,
      position,
    } satisfies IBorderActionMenuProps,
  };
};
